import type {Ref} from 'vue'
import {persistentStore, simpleTourStore} from '@/composables/store-accessor'
import {axiosInstance} from '@/utils/axios-accessor'
import {appInstance} from '@/utils/app-accessor'
import {
    B2B_AUTH_EVENT,
    CHANGE_LOCALE_EVENT,
    CHANGE_PRICE_EVENT,
    EventBus,
    FILTER_EVENT,
    RESET as RESET_EVENT,
    RESET_BOOKING_DATA,
    SEARCH_EVENT,
} from '@/utils/event-bus'
import {useProductRuntimeStore} from '~/composables/product-runtime-store'
import {
    filters as filtersBlank,
    searchRequest as searchRequestBlank,
    searchResponse as searchResponseBlank,
} from '~src/utils/simpleTours/simple-tour-blank-states'

const runtimeFiltersBlank = () => ({})

export const useSimpleTourRuntimeStore = defineStore('simpleTourRuntime', () => {
    const city: Ref = ref({})
    const productRuntimeStore = useProductRuntimeStore()
    const {
        country,
        SET_OFFERS,
        START_SEARCH,
        STOP_SEARCH,
        START_FILTER,
        STOP_FILTER,
        loadCountry,
        filterActive,
        searchActiveCount,
        searchActive,
    } = productRuntimeStore

    const searchCTS: Ref = ref(null)
    const runtimeFilters: Ref = ref(runtimeFiltersBlank())

    function SET_CITY(val: any) {
        city.value = val
    }

    async function loadCity(id: any) {
        try {
            const {
                cities: [_city],
            } = await appInstance.$api.locations.get({id, limitCities: 1})
            city.value = _city
        } catch (e) {
            city.value = {}
        }
    }

    function _newSearch(searchRequest: any) {
        stopSearch()
        SET_OFFERS([])
        SET_RUNTIME_FILTERS(runtimeFiltersBlank())
        simpleTourStore.SET_SEARCH_RESPONSE(searchResponseBlank())
        simpleTourStore.NEW_SEARCH(searchRequest)
    }

    function _setSearchResponse(searchResponse: any) {
        // if (simpleTourStore.searchResponse.product.length === searchResponse.products.length) return
        simpleTourStore.SET_FILTER({key: 'price', value: searchResponse.filters.price})
        simpleTourStore.SET_SEARCH_RESPONSE(searchResponse)
        const {products} = searchResponse
        appInstance.$localForage.setItem('simpleTour', products)
        load(products)
    }

    function SET_RUNTIME_FILTERS(filters: any) {
        runtimeFilters.value = filters
    }

    function SET_SEARCH_CTS(cancelTokenSource: any) {
        searchCTS.value = cancelTokenSource
    }

    function clientInit() {
        EventBus.$on(RESET_EVENT, reset)
        EventBus.$on(RESET_BOOKING_DATA, reset)
        EventBus.$on(B2B_AUTH_EVENT, reset)
        EventBus.$on(CHANGE_LOCALE_EVENT, reload)
        EventBus.$on(CHANGE_PRICE_EVENT, changePrice)
        appInstance.$workers.simpleToursWorker.onmessage = ({data}: any) => {
            if (data === 'load') {
                if (
                    isEqual(
                        {...filtersBlank(), price: simpleTourStore.searchResponse.filters.price},
                        simpleTourStore.filters
                    )
                ) {
                    sort()
                } else {
                    filter()
                }
                return
            } else if (data === 'refresh') {
                return
            }
            STOP_FILTER()
            if (filterActive.value) return
            SET_OFFERS(data)
            EventBus.$emit(FILTER_EVENT)
        }
    }

    async function changePrice({offerKey, prepareBookResponse}: any) {
        simpleTourStore.REFRESH_BASKET_PRICE({offerKey, prepareBookResponse})
        persistentStore.REFRESH_CONDITIONS({offerKey, prepareBookResponse})
    }

    function reset() {
        if (searchCTS.value) searchCTS.value.cancel()
        _newSearch(null)
        simpleTourStore.RESET()
    }

    async function reload() {
        if (city.value.id) await loadCity(city.value.id)
        if (country.value.id) await loadCountry(country.value.id)
    }

    async function search(rq: any) {
        START_SEARCH()
        _newSearch(rq)
        EventBus.$emit(SEARCH_EVENT)
        try {
            const cancelTokenSource = axiosInstance.CancelToken.source()
            SET_SEARCH_CTS(cancelTokenSource)
            const searchResponse = await appInstance.$api.searchTours.get(rq, cancelTokenSource.token)
            _setSearchResponse(searchResponse)
            // eslint-disable-next-line no-empty
        } catch (e) {
        } finally {
            STOP_SEARCH()
        }
    }

    function newSearch() {
        _newSearch(searchRequestBlank())
    }

    function stopSearch() {
        if (searchCTS.value) searchCTS.value.cancel()
    }

    function load(offers = []) {
        appInstance.$workers.simpleToursWorker.postMessage({action: 'load', offers: clone(offers)})
    }

    function sort() {
        START_FILTER()
        appInstance.$workers.simpleToursWorker.postMessage({
            action: 'filter',
            filters: {...clone(simpleTourStore.filters), ...runtimeFilters.value},
            sortKey: simpleTourStore.sortFnName,
        })
    }

    function filter() {
        START_FILTER()
        appInstance.$workers.simpleToursWorker.postMessage({
            action: 'filter',
            filters: {...clone(simpleTourStore.filters), ...runtimeFilters.value},
            sortKey: simpleTourStore.sortFnName,
        })
    }

    const searchPageLink = computed(() => (searchRequest: any) => {
        const {convertToCurrency, ...query} = searchRequest
        return {name: 'simpleTourSearch', query}
    })

    const infoPageLink = computed(() => (product: any, searchRequest: any) => {
        const {query} = searchPageLink.value(searchRequest)
        if (product.productId) query.productId = product.productId
        query.name = product.info.name
        return {name: 'simpleTour', query}
    })

    const searchRequestFromQuery = computed(() => (query: any) => {
        //TODO Need filter $route.query params
        const rq = Object.assign(searchRequestBlank(), query)
        if (typeof rq.childrenAges === 'string') {
            rq.childrenAges = [parseInt(rq.childrenAges, 10)]
        } else {
            rq.childrenAges = rq.childrenAges.map((age: string) => parseInt(age, 10))
        }
        rq.convertToCurrency = persistentStore.currency
        rq.adults = parseInt(rq.adults, 10)
        if (rq.cityId) rq.cityId = parseInt(rq.cityId, 10)
        delete rq.name
        delete rq.productId

        return rq
    })

    return {
        ...productRuntimeStore,
        city,
        SET_CITY,
        RESET,
        SET_RUNTIME_FILTERS,
        SET_SEARCH_CTS,
        clientInit,
        loadCity,
        changePrice,
        reload,
        reset,
        newSearch,
        search,
        searchPageLink,
        searchActiveCount,
        searchActive,
        infoPageLink,
        searchRequestFromQuery,
        load,
        sort,
        filter,
    }
})

// TODO [P3] Need check this HRM improvement. Relates with https://pinia.vuejs.org/cookbook/hot-module-replacement.html#HMR-Hot-Module-Replacement-
if (import.meta.hot) import.meta.hot.accept(acceptHMRUpdate(useSimpleTourRuntimeStore, import.meta.hot))
